import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiMenu, FiX } from 'react-icons/fi';
import { GiWheat } from 'react-icons/gi';
import { useCartStore } from '../../store/cartStore';
import { useAuthStore } from '../../store/authStore';
import { scrollToHomeSection } from '../../utils/scrollToHomeSection';

const getPageNavStyle = (pathname) => {
  if (pathname === '/' || pathname.startsWith('/farmer/')) {
    return {
      topBackground: 'transparent',
      topTextColor: '#ffffff',
      topLinkWeight: 500,
      topLoginBorder: '#ffffff',
      topProfileBg: 'rgba(255,255,255,0.2)',
    };
  }

  if (pathname.startsWith('/rice-store')) {
    return {
      topBackground: '#fffbeb',
      topTextColor: '#92400e',
      topLinkWeight: 700,
      topLoginBorder: '#d97706',
      topProfileBg: '#fef3c7',
    };
  }

  if (pathname.startsWith('/grain-store')) {
    return {
      topBackground: '#f0fdf4',
      topTextColor: '#166534',
      topLinkWeight: 700,
      topLoginBorder: '#2d6a4f',
      topProfileBg: '#dcfce7',
    };
  }

  if (pathname.startsWith('/ritu-roots')) {
    return {
      topBackground: '#ecfeff',
      topTextColor: '#0f766e',
      topLinkWeight: 700,
      topLoginBorder: '#0f766e',
      topProfileBg: '#ccfbf1',
    };
  }

  return {
    topBackground: '#ffffff',
    topTextColor: '#374151',
    topLinkWeight: 600,
    topLoginBorder: '#2d6a4f',
    topProfileBg: '#d8f3dc',
  };
};

const NAV_ACCENTS = {
  rice: {
    activeBackground: 'linear-gradient(135deg, #f59e0b, #d97706)',
    activeText: '#ffffff',
    activeShadow: '0 10px 24px rgba(217, 119, 6, 0.22)',
  },
  grain: {
    activeBackground: 'linear-gradient(135deg, #2f855a, #1f6f46)',
    activeText: '#ffffff',
    activeShadow: '0 10px 24px rgba(31, 111, 70, 0.2)',
  },
  roots: {
    activeBackground: 'linear-gradient(135deg, #0f766e, #115e59)',
    activeText: '#ffffff',
    activeShadow: '0 10px 24px rgba(15, 118, 110, 0.18)',
  },
   products: {
    activeBackground: 'linear-gradient(135deg, #0f766e, #115e59)',
    activeText: '#ffffff',
    activeShadow: '0 10px 24px rgba(15, 118, 110, 0.18)',
  },
  neutral: {
    activeBackground: 'linear-gradient(135deg, #0f172a, #1e293b)',
    activeText: '#ffffff',
    activeShadow: '0 10px 24px rgba(15, 23, 42, 0.18)',
  },
};

const getDesktopNavItemStyle = ({ isActive, accent, textColor, topLinkWeight }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: isActive ? '42px' : 'auto',
  padding: isActive ? '10px 18px' : '0',
  borderRadius: isActive ? '999px' : '0',
  textDecoration: 'none',
  fontSize: '14px',
  fontWeight: isActive ? 700 : topLinkWeight,
  color: isActive ? accent.activeText : textColor,
  background: isActive ? accent.activeBackground : 'transparent',
  boxShadow: isActive ? accent.activeShadow : 'none',
  border: isActive ? '1px solid transparent' : '1px solid transparent',
  letterSpacing: '0.01em',
  transition: 'all 0.25s ease',
});

const getMobileNavItemStyle = (isActive, accent) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '12px 14px',
  borderRadius: '16px',
  textDecoration: 'none',
  fontWeight: isActive ? 700 : 600,
  color: isActive ? accent.activeText : '#374151',
  background: isActive ? accent.activeBackground : '#f8fafc',
  boxShadow: isActive ? accent.activeShadow : 'inset 0 0 0 1px rgba(226, 232, 240, 0.9)',
  transition: 'all 0.25s ease',
});

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeHomeSection, setActiveHomeSection] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const getTotalItems = useCartStore((s) => s.getTotalItems);
  const { userProfile } = useAuthStore();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveHomeSection('');
      return undefined;
    }

    const sectionIds = ['products', 'testimonials'];
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((firstEntry, secondEntry) => secondEntry.intersectionRatio - firstEntry.intersectionRatio);

        if (visibleEntries.length > 0) {
          setActiveHomeSection(visibleEntries[0].target.id);
          return;
        }

        if (window.scrollY < 180) {
          setActiveHomeSection('');
        }
      },
      {
        root: null,
        rootMargin: '-25% 0px -45% 0px',
        threshold: [0.2, 0.35, 0.55],
      },
    );

    const observedElements = sectionIds
      .map((sectionId) => document.getElementById(sectionId))
      .filter(Boolean);

    observedElements.forEach((element) => observer.observe(element));

    const handleTopReset = () => {
      if (window.scrollY < 180) {
        setActiveHomeSection('');
      }
    };

    window.addEventListener('scroll', handleTopReset, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleTopReset);
      observedElements.forEach((element) => observer.unobserve(element));
      observer.disconnect();
    };
  }, [location.pathname]);

  const displayName = userProfile?.name || 'User';
  const isLoggedIn = Boolean(userProfile);
  const cartCount = isLoggedIn ? getTotalItems() : 0;
  const role = userProfile?.role || '';
  const isStaff = userProfile && ['ceo', 'admin', 'manager', 'supervisor', 'farmer', 'dealer', 'delivery', 'super_admin'].includes(userProfile.role);
  const isFocusedDashboardRole = role === 'farmer' || role === 'delivery';
  const showPublicNavLinks = !isFocusedDashboardRole;
  const showJoinUsCta = !isLoggedIn || role === 'farmer';
  const canUseCart = !isLoggedIn || role === 'customer' || role === 'farmer';
  const dashboardPath =
    userProfile?.role === 'super_admin'
      ? '/dashboard/super-admin'
      : userProfile?.role === 'admin'
        ? '/dashboard/admin'
        : `/dashboard/${userProfile?.role || ''}`;
  const pageNavStyle = useMemo(() => getPageNavStyle(location.pathname), [location.pathname]);
  const onOverlayTop = !scrolled && pageNavStyle.topBackground === 'transparent';
  const navBackgroundColor = scrolled ? '#ffffff' : pageNavStyle.topBackground;
  const textColor = scrolled ? '#374151' : pageNavStyle.topTextColor;
  const menuIconColorClass = onOverlayTop ? 'text-white' : 'text-gray-800';
  const loginBorderColor = scrolled ? '#2d6a4f' : pageNavStyle.topLoginBorder;
  const profileBackground = scrolled ? '#d8f3dc' : pageNavStyle.topProfileBg;
  const topLinkWeight = scrolled ? 600 : pageNavStyle.topLinkWeight;
  const pathname = location.pathname;
  const isRiceStoreActive = pathname.startsWith('/rice-store');
  const isGrainStoreActive = pathname.startsWith('/grain-store');
  const isRituRootsActive = pathname.startsWith('/ritu-roots');
  const isProductsActive = pathname.startsWith('/products');
  const isDashboardActive = pathname.startsWith('/dashboard');
  const openHomeSection = (sectionId) => (event) => {
    event.preventDefault();
    setMenuOpen(false);
    setActiveHomeSection(sectionId);

    if (location.pathname === '/') {
      scrollToHomeSection(sectionId);
      return;
    }

    navigate('/', { state: { scrollToSection: sectionId } });
  };

  const openHomeTop = (event) => {
    event.preventDefault();
    setMenuOpen(false);
    setActiveHomeSection('');

    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    navigate('/');
    window.setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 60);
  };

  const scrollToFarmerEquipmentZone = () => {
    window.setTimeout(() => {
      document
        .getElementById('farmer-equipment-zone')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  };

  const openBrandDestination = (event) => {
    if (role !== 'farmer') {
      openHomeTop(event);
      return;
    }

    event.preventDefault();
    setMenuOpen(false);

    if (location.pathname === '/dashboard/farmer') {
      scrollToFarmerEquipmentZone();
      return;
    }

    navigate('/dashboard/farmer', { state: { scrollToFarmerEquipment: true } });
  };

  const publicNavItems = [
    {
      label: 'Direct Rice',
      to: '/rice-store',
      isActive: isRiceStoreActive,
      accent: NAV_ACCENTS.rice,
      type: 'link',
    },
    {
      label: 'Grain Store',
      to: '/grain-store',
      isActive: isGrainStoreActive,
      accent: NAV_ACCENTS.grain,
      type: 'link',
    },
    {
      label: 'Seasonal Store',
      to: '/ritu-roots',
      isActive: isRituRootsActive,
      accent: NAV_ACCENTS.roots,
      type: 'link',
    },
    {
      label: 'KisanKart',
      to: '/products',
      isActive: isProductsActive,
      accent: NAV_ACCENTS.neutral,
      type: 'link',
    },
    {
      label: 'Reviews',
      href: '#testimonials',
      onClick: openHomeSection('testimonials'),
      isActive: activeHomeSection === 'testimonials',
      accent: NAV_ACCENTS.neutral,
      type: 'action',
    },
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${onOverlayTop ? 'py-5' : 'py-3 shadow-md'}`}
      style={{ background: navBackgroundColor }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to={role === 'farmer' ? '/dashboard/farmer' : '/'} onClick={openBrandDestination} className="flex items-center gap-2">
          <GiWheat className="text-primary text-3xl" />
          <span className="text-2xl font-bold text-primary">Sedyam</span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {showPublicNavLinks && (
            <>
              {publicNavItems.map((item) => {
                const navItemStyle = getDesktopNavItemStyle({
                  isActive: item.isActive,
                  accent: item.accent,
                  textColor,
                  topLinkWeight,
                });

                if (item.type === 'link') {
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      style={navItemStyle}
                      aria-current={item.isActive ? 'page' : undefined}
                    >
                      {item.label}
                    </Link>
                  );
                }

                return (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={item.onClick}
                    style={navItemStyle}
                    aria-current={item.isActive ? 'page' : undefined}
                  >
                    {item.label}
                  </a>
                );
              })}
            </>
          )}

          {showJoinUsCta && (
            <Link
              to="/join-us"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '40px',
                padding: '9px 16px',
                borderRadius: '999px',
                textDecoration: 'none',
                fontSize: '13px',
                fontWeight: 700,
                color: '#166534',
                background: 'linear-gradient(135deg, rgba(254,243,199,0.95), rgba(220,252,231,0.95))',
                border: '1px solid rgba(22, 101, 52, 0.12)',
                boxShadow: '0 10px 24px rgba(22, 101, 52, 0.08)',
                letterSpacing: '0.01em',
                transition: 'all 0.25s ease',
              }}
            >
              Join Us
            </Link>
          )}

          {canUseCart && (
            <Link to="/cart" className="relative flex items-center gap-2 hover:opacity-80 transition" style={{ color: textColor }}>
              <FiShoppingCart size={24} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-sm">
                  {cartCount}
                </span>
              )}
            </Link>
          )}

          {isLoggedIn ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {isStaff && (
                <Link
                  to={dashboardPath}
                  style={getDesktopNavItemStyle({
                    isActive: isDashboardActive,
                    accent: NAV_ACCENTS.grain,
                    textColor: scrolled ? '#2d6a4f' : textColor,
                    topLinkWeight,
                  })}
                  aria-current={isDashboardActive ? 'page' : undefined}
                >
                  Dashboard
                </Link>
              )}

              <Link to="/profile" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', background: profileBackground, padding: '6px 16px', borderRadius: '999px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'black', fontWeight: '700', fontSize: '13px' }}>
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <span style={{ color: scrolled ? '#1a1a2e' : textColor, fontWeight: topLinkWeight, fontSize: '13px' }}>
                  {displayName.split(' ')[0]}
                </span>
              </Link>
            </div>
          ) : (
            <Link to="/login" style={{ background: 'transparent', border: `1.5px solid ${loginBorderColor}`, color: scrolled ? '#2d6a4f' : textColor, padding: '8px 20px', borderRadius: '999px', fontWeight: topLinkWeight, fontSize: '14px', textDecoration: 'none' }}>
              Login
            </Link>
          )}
        </div>

        <button className={`md:hidden text-2xl ${menuIconColorClass}`} onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white shadow-lg px-6 py-4 flex flex-col gap-4 absolute w-full top-full left-0 border-t border-gray-100">
          {showPublicNavLinks && (
            <>
              {publicNavItems.map((item) => {
                if (item.type === 'link') {
                  return (
                    <Link
                      key={item.label}
                      to={item.to}
                      onClick={() => setMenuOpen(false)}
                      style={getMobileNavItemStyle(item.isActive, item.accent)}
                      aria-current={item.isActive ? 'page' : undefined}
                    >
                      <span>{item.label}</span>
                      {item.isActive ? <span>Current</span> : null}
                    </Link>
                  );
                }

                return (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={item.onClick}
                    style={getMobileNavItemStyle(item.isActive, item.accent)}
                    aria-current={item.isActive ? 'page' : undefined}
                  >
                    <span>{item.label}</span>
                    {item.isActive ? <span>Current</span> : null}
                  </a>
                );
              })}
            </>
          )}


          {canUseCart && (
            <Link to="/cart" onClick={() => setMenuOpen(false)} className="text-green-700 font-bold flex items-center gap-2">
              <FiShoppingCart /> Cart{cartCount > 0 ? ` (${cartCount})` : ''}
            </Link>
          )}
          <hr className="my-2 border-gray-200" />

          {isLoggedIn ? (
            <>
              {isStaff && (
                <Link
                  to={dashboardPath}
                  onClick={() => setMenuOpen(false)}
                  style={getMobileNavItemStyle(isDashboardActive, NAV_ACCENTS.grain)}
                  aria-current={isDashboardActive ? 'page' : undefined}
                >
                  <span>Dashboard</span>
                  {isDashboardActive ? <span>Current</span> : null}
                </Link>
              )}
              <Link to="/profile" onClick={() => setMenuOpen(false)} className="text-gray-700 font-medium flex items-center gap-2">
                Profile ({displayName})
              </Link>
            </>
          ) : (
            <Link to="/login" onClick={() => setMenuOpen(false)} className="bg-green-700 text-white px-4 py-2 rounded-lg text-center font-bold">Login</Link>
          )}

          {showJoinUsCta && (
            <Link
              to="/join-us"
              onClick={() => setMenuOpen(false)}
              className="bg-gradient-to-r from-amber-100 to-emerald-100 text-emerald-800 font-bold px-4 py-3 rounded-2xl text-center border border-emerald-100"
            >
              Join Us
            </Link>
          )}

        </div>
      )}
    </nav>
  );
}
